import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/modern/components/ui/dropdown-menu";
import { Button } from "@/modern/components/ui/button-shadcn";
import { useConfirm } from "@omit/react-confirm-dialog";
import {
  MoreHorizontal,
  MoreVertical,
  Trash2,
  Download,
  Pencil,
  Sparkles,
  Megaphone,
  Calculator,
  Share2,
  Copy,
  ScanText,
  Eye,
} from "lucide-react";
import type { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import { isImageFileTyped } from "./FileHelpers";
import { getFileExtension, cn } from "@/modern/utils";
import { copyImageToClipboard } from "@/react/components/entityViews/files/utils/copyImageToClipboard";

const EXTRACTABLE_EXTENSIONS = new Set(["pdf", "xlsx", "xls", "docx", "csv", "tsv", "txt"]);

export type FileAction =
  | "view"
  | "annotate"
  | "analyze"
  | "promote"
  | "adjustPoints"
  | "summaryShare"
  | "extract"
  | "download"
  | "copy"
  | "delete";

interface FileActionsMenuProps {
  file: SwivuFile;
  onFileDownload: (file: SwivuFile) => void;
  onFileDelete: (fileId: number) => void;
  /** Opens File Detail. Omit when the menu is already inside File Detail. */
  onView?: (file: SwivuFile) => void;
  onAnnotate?: (file: SwivuFile) => void;
  onAnalyze?: (file: SwivuFile) => void;
  onPromote?: (file: SwivuFile) => void;
  onAdjustPoints?: (file: SwivuFile) => void;
  onSummaryShare?: (file: SwivuFile) => void;
  onExtract?: (file: SwivuFile) => void;
  onCopy?: (file: SwivuFile) => void;
  shouldShowAdjustPoints?: (file: SwivuFile) => boolean;
  /** @deprecated Use onAnnotate — kept for transitional callers */
  onFileAnnotate?: (file: SwivuFile) => void;
  /** @deprecated Prefer onView */
  onFileOpen?: (file: SwivuFile) => void;
  triggerClassName?: string;
  align?: "start" | "center" | "end";
  /** When true, trigger is always visible (e.g. inside modal overlay). */
  alwaysVisible?: boolean;
  /** Default vertical (grid tiles). File detail header uses horizontal. */
  iconOrientation?: "vertical" | "horizontal";
}

export const FileActionsMenu = ({
  file,
  onFileDownload,
  onFileDelete,
  onView,
  onAnnotate,
  onAnalyze,
  onPromote,
  onAdjustPoints,
  onSummaryShare,
  onExtract,
  onCopy,
  shouldShowAdjustPoints,
  onFileAnnotate,
  onFileOpen,
  triggerClassName,
  align = "end",
  alwaysVisible = false,
  iconOrientation = "vertical",
}: FileActionsMenuProps) => {
  const confirm = useConfirm();
  const fileExt = getFileExtension(file.name);
  const isPDF = fileExt === "pdf";
  const isImage = isImageFileTyped(file) && !isPDF;
  const isExtractable = EXTRACTABLE_EXTENSIONS.has(fileExt);
  const showAdjustPoints = Boolean(shouldShowAdjustPoints?.(file) && onAdjustPoints);
  const annotateHandler = onAnnotate ?? onFileAnnotate;
  const viewHandler = onView ?? onFileOpen;
  const TriggerIcon = iconOrientation === "horizontal" ? MoreHorizontal : MoreVertical;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          className={cn(
            alwaysVisible ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
            triggerClassName,
          )}
          onClick={(e) => {
            e.stopPropagation();
          }}
          variant="secondary"
          size="icon-sm"
          aria-label="File actions"
        >
          <TriggerIcon className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className="w-52 z-[calc(var(--swivu-bryntum-z-index)+30)]"
        onClick={(e) => e.stopPropagation()}
      >
        {viewHandler && (
          <>
            <DropdownMenuItem
              disabled={file.canViewGrant === 0}
              onClick={(e) => {
                e.stopPropagation();
                viewHandler(file);
              }}
            >
              <Eye className="mr-2 h-4 w-4" />
              View
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        {isImage && (
          <>
            {annotateHandler && (
              <DropdownMenuItem
                disabled={file.canEditGrant === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  annotateHandler(file);
                }}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Annotate
              </DropdownMenuItem>
            )}
            {onAnalyze && (
              <DropdownMenuItem
                disabled={file.canViewGrant === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  onAnalyze(file);
                }}
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Analyze
              </DropdownMenuItem>
            )}
            {onPromote && (
              <DropdownMenuItem
                disabled={file.canEditGrant === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  onPromote(file);
                }}
              >
                <Megaphone className="mr-2 h-4 w-4" />
                Promote
              </DropdownMenuItem>
            )}
            {showAdjustPoints && (
              <DropdownMenuItem
                disabled={file.canEditGrant === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  onAdjustPoints?.(file);
                }}
              >
                <Calculator className="mr-2 h-4 w-4" />
                Adjust Points
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
          </>
        )}

        {!isImage && isExtractable && onExtract && (
          <DropdownMenuItem
            disabled={file.canViewGrant === 0}
            onClick={(e) => {
              e.stopPropagation();
              onExtract(file);
            }}
          >
            <ScanText className="mr-2 h-4 w-4" />
            Extract
          </DropdownMenuItem>
        )}

        {onSummaryShare && (
          <DropdownMenuItem
            disabled={file.canViewGrant === 0}
            onClick={(e) => {
              e.stopPropagation();
              onSummaryShare(file);
            }}
          >
            <Share2 className="mr-2 h-4 w-4" />
            {isImage ? "Summary & Share" : "Share"}
          </DropdownMenuItem>
        )}

        <DropdownMenuItem
          disabled={file.canViewGrant === 0}
          onClick={(e) => {
            e.stopPropagation();
            onFileDownload(file);
          }}
        >
          <Download className="mr-2 h-4 w-4" />
          Download
        </DropdownMenuItem>

        {isImage && "clipboard" in navigator && (
          <DropdownMenuItem
            disabled={file.canViewGrant === 0}
            onClick={(e) => {
              e.stopPropagation();
              if (onCopy) {
                onCopy(file);
              } else {
                void copyImageToClipboard(file);
              }
            }}
          >
            <Copy className="mr-2 h-4 w-4" />
            Copy
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={file.canEditGrant === 0}
          onClick={async (e) => {
            e.stopPropagation();
            if (
              file.canEditGrant !== 0 &&
              (await confirm({
                title: "Confirm Delete",
                description: `Are you sure you want to delete "${file.name}"?`,
                customActions: ({ confirm: confirmAction, cancel }: { confirm: () => void; cancel: () => void }) => (
                  <div className="space-x-4">
                    <Button variant="ghost" onClick={cancel}>
                      Cancel
                    </Button>
                    <Button
                      className="dark:bg-red-900 dark:text-red-200 bg-red-100 text-red-800 hover:dark:bg-red-950 hover:dark:text-red-300 hover:bg-red-200 transition-colors"
                      onClick={confirmAction}
                    >
                      Delete
                    </Button>
                  </div>
                ),
              }))
            ) {
              onFileDelete(file.id);
            }
          }}
          className="text-destructive focus:text-destructive dark:focus:text-destructive-foreground"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
